import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, computed, inject, signal, viewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

// PrimeNG
import { CardModule } from 'primeng/card';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { MessageModule } from 'primeng/message';
import { MultiSelectModule } from 'primeng/multiselect';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectButtonModule } from 'primeng/selectbutton';
import { IftaLabelModule } from 'primeng/iftalabel';
import { DividerModule } from 'primeng/divider';
import { TooltipModule } from 'primeng/tooltip';
import { CheckboxModule } from 'primeng/checkbox';
import { PanelModule } from 'primeng/panel';

import { MeasurementsService } from '@cosmos/angular/api';
import { RBACService } from '@cosmos/angular/utils';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MONITORING_EXPLORER_TYPES } from '@cosmos/shared';

import { Chart, registerables } from 'chart.js';
import 'chartjs-adapter-moment';

import { findRouteParam } from '../../navigation/route-param';
import type { ChartOptionsConfig } from '../../data/charts/chart-options/chart-options-storage.service';
import { ExplorerAxisRangeComponent } from './explorer-axis-range.component';
import { ExplorerDiagnostics, StageCountView, withDeltas } from './explorer-diagnostics';
import { ChartLegendComponent } from '../../data/charts/chart-legend/chart-legend.component';
import { HtmlLegendItem, HtmlLegendPlugin } from '../../data/charts/chart-legend/chartjs-plugin-html-legend';
import { composeChartWithLegend, downloadDataUrl, downloadTextFile } from '../../data/charts/chart-export/chart-export';
import {
  AUTO_RANGE, ExplorerAxisMeta, ExplorerChartDataset, applyRangeToScale, axisTitle, buildExplorerChartConfig,
  buildExplorerCsv, resetRangesOnVariableChange,
} from './explorer-chart-config';
import {
  ExploreFormInput, ExplorerDatasetResponse, ExplorerFieldOption, buildExploreRequest, buildQueryBadges, composeSeriesLabel,
  explorerTypesForProject, fieldOptions, variableGroups,
} from './explorer-options';

/** Diagnostics as rendered: stage counts with deltas and pre-serialised JSON (not re-stringified on every change detection). */
interface DiagnosticsView extends ExplorerDiagnostics {
  counts: StageCountView[];
  requestJson: string;
  pipelineJson: string;
  withoutRows: number;
}

interface FilterState {
  availableValues: Array<{ value: string | number; count: number }>;
  loading: boolean;
}

const TIME_AGGREGATIONS = ['none', 'day', 'week', 'month', 'quarter', 'group'];
const AGGREGATION_FUNCTIONS = ['average', 'sum', 'min', 'max', 'count'];
const NUMERIC_OPERATORS = ['gt', 'gte', 'lt', 'lte', 'eq', 'neq'];
const FILTER_LOGIC_OPTIONS = ['and', 'or'];

@Component({
  selector: 'cosmos-monitoring-data-explorer',
  imports: [
    FormsModule, ReactiveFormsModule, CardModule, SelectModule, DatePickerModule,
    ButtonModule, ProgressSpinnerModule, MessageModule, TranslateModule,
    IftaLabelModule, DividerModule, MultiSelectModule, InputNumberModule, SelectButtonModule,
    TooltipModule, CheckboxModule, PanelModule, ExplorerAxisRangeComponent, ChartLegendComponent,
  ],
  templateUrl: './monitoring-data-explorer.component.html',
  styleUrls: ['./monitoring-data-explorer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MonitoringDataExplorerComponent {
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly measurementsService = inject(MeasurementsService);
  readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly rbac = inject(RBACService);

  private readonly chartCanvas = viewChild<ElementRef<HTMLCanvasElement>>('chartCanvas');

  private readonly t = (key: string) => this.translateService.instant(key);

  readonly loading = signal(false);
  readonly hasData = signal(false);
  readonly errorMessage = signal('');
  readonly showAggregationFunction = signal(false);
  readonly isGroupMode = signal(false);
  readonly secondaryEnabled = signal(false);

  explorerForm!: FormGroup;
  readonly projectId: string;

  /** Superadmin-only debug mode: the query also returns diagnostics (pipeline, per-stage counts, rows per measurement). */
  readonly debugAllowed: boolean;
  readonly diagnostics = signal<DiagnosticsView[] | null>(null);

  /** Query settings collapse after a successful query; the header then shows a summary + badges for what was set up. */
  readonly queryCollapsed = signal(false);
  readonly querySummary = signal<{ text: string; badges: string[] } | null>(null);

  /** Custom (DOM) legend: capped height with inner scroll; also what the PNG export stitches under the chart. */
  readonly legendItems = signal<HtmlLegendItem[]>([]);
  private readonly resultTimeAgg = signal('none');
  /** Bar (group) mode has one dataset per chart — a legend adds nothing there. */
  readonly showLegend = computed(() => this.resultTimeAgg() !== 'group');

  readonly timeAggregations: Array<{ key: string; label: string }>;
  readonly aggregationFunctions: Array<{ key: string; label: string }>;
  readonly numericOperators: Array<{ key: string; label: string }>;
  readonly filterLogicOptions: Array<{ key: string; label: string }>;

  // ── catalog-derived options ──────────────────────────────────────────────
  private readonly projectTypes = signal<string[]>([]);
  readonly selectedType = signal<string | null>(null);
  private readonly selectedMetric = signal<string | null>(null);

  readonly typeOptions = computed(() =>
    explorerTypesForProject(this.projectTypes()).map((value) => ({ value, label: this.t('measurement.types.' + value) })));
  readonly variableOptions = computed(() => variableGroups(this.selectedType(), this.t));
  readonly secondaryOptions = computed(() => {
    const primary = this.selectedMetric();
    return variableGroups(this.selectedType(), this.t)
      .map((g) => ({ ...g, items: g.items.filter((i) => i.key !== primary) }))
      .filter((g) => g.items.length > 0);
  });
  readonly fields = computed<ExplorerFieldOption[]>(() => fieldOptions(this.selectedType(), this.t));
  readonly canAddSecondary = computed(() => !!this.selectedMetric() && !this.isGroupMode());

  filterStates: FilterState[] = [];
  readonly maxFilters = 2;

  // ── last result + display settings ───────────────────────────────────────
  readonly primaryAxis = signal<ExplorerAxisMeta | null>(null);
  readonly secondaryAxis = signal<ExplorerAxisMeta | null>(null);
  readonly yRange = signal<ChartOptionsConfig>(AUTO_RANGE);
  readonly y2Range = signal<ChartOptionsConfig>(AUTO_RANGE);
  readonly leftAxisTitle = computed(() => { const p = this.primaryAxis(); return p ? axisTitle(p, true) : ''; });
  readonly rightAxisTitle = computed(() => { const s = this.secondaryAxis(); return s ? axisTitle(s, true) : ''; });
  readonly hasSecondaryResult = computed(() => !!this.secondaryAxis());

  private lastKeys: { primary: string | null; secondary: string | null } = { primary: null, secondary: null };
  private lastResult: { datasets: ExplorerChartDataset[]; timeAgg: string } | null = null;
  private chart: Chart | null = null;

  constructor() {
    Chart.register(...registerables);

    // `:project` lives on an ancestor route; the `monitoring` layout route hosts a component,
    // so Angular doesn't inherit it into this child's snapshot params — walk up the tree instead.
    this.projectId = findRouteParam(this.route, 'project') ?? '';

    try {
      this.debugAllowed = this.rbac.isSuperAdmin();
    } catch {
      this.debugAllowed = false; // no user in state yet
    }

    const tr = (prefix: string, keys: string[]) => keys.map((key) => ({ key, label: this.t(prefix + key) }));
    this.timeAggregations = tr('analytics.labels.aggregation.', TIME_AGGREGATIONS);
    this.aggregationFunctions = tr('analytics.labels.aggregation.functions.', AGGREGATION_FUNCTIONS);
    this.numericOperators = tr('analytics.labels.operators.', NUMERIC_OPERATORS);
    this.filterLogicOptions = tr('analytics.labels.logic.', FILTER_LOGIC_OPTIONS);

    this.initForm();
    this.setupFormListeners();
    this.fetchAvailableTypes();

    this.destroyRef.onDestroy(() => this.destroyChart());
  }

  private fetchAvailableTypes() {
    if (!this.projectId) {
      this.errorMessage.set('monitoring.data-explorer.error-loading');
      return;
    }
    this.measurementsService.getAvailableMeasurementTypes(this.projectId).subscribe({
      next: (response) => this.projectTypes.set(response.body || []),
      error: (e) => console.error('Error fetching measurement types', e),
    });
  }

  private initForm() {
    this.explorerForm = this.fb.group({
      measurementType: [null, [Validators.required]],
      dateRange: [null],
      metricField: [{ value: null, disabled: true }, [Validators.required]],
      secondaryMetricField: [null],
      groupByField: [null],
      groupByField2: [null],
      timeAggregation: [this.timeAggregations[0]],
      aggregation: [this.aggregationFunctions[0]],
      filterLogic: ['and'],
      filters: this.fb.array([]),
      debug: [false],
    });
  }

  get filtersArray(): FormArray {
    return this.explorerForm.get('filters') as FormArray;
  }

  private setupFormListeners() {
    this.explorerForm.get('measurementType')?.valueChanges.subscribe((type) => {
      this.selectedType.set(type ?? null);
      const metric = this.explorerForm.get('metricField')!;
      metric.setValue(null);
      this.disableSecondary();
      this.explorerForm.get('groupByField')?.setValue(null);   // channel is NEVER preselected
      this.explorerForm.get('groupByField2')?.setValue(null);
      if (type) metric.enable(); else metric.disable();
      this.clearAllFilters();
    });

    this.explorerForm.get('metricField')?.valueChanges.subscribe((key) => {
      this.selectedMetric.set(key ?? null);
      if (!key || key === this.explorerForm.get('secondaryMetricField')?.value) this.disableSecondary();
    });

    this.explorerForm.get('groupByField')?.valueChanges.subscribe(() => {
      this.explorerForm.get('groupByField2')?.setValue(null);
    });

    this.explorerForm.get('timeAggregation')?.valueChanges.subscribe((timeAgg) => {
      this.showAggregationFunction.set(timeAgg?.key !== 'none');
      const grouped = timeAgg?.key === 'group';
      this.isGroupMode.set(grouped);
      if (grouped) this.disableSecondary();   // a secondary axis is not supported in bar (group) mode
    });
  }

  // ── secondary variable ───────────────────────────────────────────────────
  enableSecondary() {
    this.secondaryEnabled.set(true);
  }

  disableSecondary() {
    this.secondaryEnabled.set(false);
    this.explorerForm?.get('secondaryMetricField')?.setValue(null);
  }

  // ── filters ──────────────────────────────────────────────────────────────
  canAddFilter(): boolean {
    return this.filtersArray.length < this.maxFilters;
  }

  getAvailableFilterFields(currentIndex: number): ExplorerFieldOption[] {
    const usedKeys: string[] = [];
    this.filtersArray.controls.forEach((control, idx) => {
      if (idx !== currentIndex) {
        const fieldValue = control.get('field')?.value;
        if (fieldValue?.key) usedKeys.push(fieldValue.key);
      }
    });
    return this.fields().filter((f) => !usedKeys.includes(f.key));
  }

  getAvailableGroupBy2Fields(): ExplorerFieldOption[] {
    const mainGroupBy = this.explorerForm.get('groupByField')?.value as ExplorerFieldOption | null;
    if (!mainGroupBy) return [];
    return this.fields().filter((f) => f.key !== mainGroupBy.key);
  }

  addFilter() {
    if (!this.canAddFilter()) return;
    this.filtersArray.push(this.fb.group({ field: [null], selectedValues: [[]], operator: ['eq'], numericValue: [null] }));
    this.filterStates.push({ availableValues: [], loading: false });
  }

  onFilterFieldChange(index: number) {
    const filterGroup = this.filtersArray.at(index) as FormGroup;
    const field = filterGroup.get('field')?.value as ExplorerFieldOption;
    if (!field) return;

    filterGroup.patchValue({ selectedValues: [], operator: 'eq', numericValue: null });

    if (field.filterType === 'categorical') {
      this.filterStates[index].loading = true;
      const measurementType = this.explorerForm.get('measurementType')?.value;
      this.measurementsService.getDistinctValues(this.projectId, field.path, measurementType).subscribe({
        next: (values: any) => {
          this.filterStates[index].availableValues = values.body || [];
          this.filterStates[index].loading = false;
        },
        error: () => (this.filterStates[index].loading = false),
      });
    }
  }

  removeFilter(index: number) {
    this.filtersArray.removeAt(index);
    this.filterStates.splice(index, 1);
  }

  clearAllFilters() {
    while (this.filtersArray.length > 0) this.filtersArray.removeAt(0);
    this.filterStates = [];
  }

  getFilterField(index: number): ExplorerFieldOption | null {
    return this.filtersArray.at(index)?.get('field')?.value || null;
  }

  // ── query ────────────────────────────────────────────────────────────────
  onApply() {
    this.fetchData();
  }

  private formInput(): ExploreFormInput {
    const v = this.explorerForm.getRawValue();
    return {
      measurementType: v.measurementType,
      metricField: v.metricField,
      secondaryMetricField: v.secondaryMetricField,
      dateRange: v.dateRange,
      groupByField: v.groupByField,
      groupByField2: v.groupByField2,
      timeAggregationKey: v.timeAggregation?.key || 'none',
      aggregationKey: v.aggregation?.key || 'average',
      filterLogic: v.filterLogic,
      filters: v.filters,
    };
  }

  private fetchData() {
    this.loading.set(true);
    this.errorMessage.set('');
    const input = this.formInput();
    const params = buildExploreRequest(input);

    const onBody = (body: ExplorerDatasetResponse[]) => {
      this.loading.set(false);
      if (body.length > 0) {
        this.applyResult(body, input);
      } else {
        this.hasData.set(false);
        this.destroyChart();
      }
    };
    const onError = (error: unknown) => {
      this.loading.set(false);
      this.hasData.set(false);
      this.errorMessage.set('monitoring.data-explorer.error-loading');
      console.error('Monitoring explorer error:', error);
    };

    if (this.debugAllowed && !!this.explorerForm.get('debug')?.value) {
      // Debug mode never throws on a failing query: the response carries the error + the failing stage.
      this.measurementsService.exploreMeasurementsDebug(this.projectId, params).subscribe({
        next: (response) => {
          this.diagnostics.set(this.toDiagnosticsViews((response.body?.diagnostics ?? []) as ExplorerDiagnostics[]));
          onBody((response.body?.datasets ?? []) as ExplorerDatasetResponse[]);
        },
        error: onError,
      });
      return;
    }

    this.diagnostics.set(null);
    this.measurementsService.exploreMeasurements(this.projectId, params).subscribe({
      next: (response: any) => onBody((response?.body ?? []) as ExplorerDatasetResponse[]),
      error: onError,
    });
  }

  private toDiagnosticsViews(list: ExplorerDiagnostics[]): DiagnosticsView[] {
    return list.map((d) => ({
      ...d,
      counts: withDeltas(d.stageCounts),
      requestJson: JSON.stringify(d.request, null, 2),
      pipelineJson: JSON.stringify(d.pipeline, null, 2),
      withoutRows: d.measurements.filter((m) => m.rows === 0).length,
    }));
  }

  private applyResult(body: ExplorerDatasetResponse[], input: ExploreFormInput) {
    const def = MONITORING_EXPLORER_TYPES[input.measurementType ?? ''];
    const meta = (key: string | null): ExplorerAxisMeta | null => {
      const v = def?.variables.find((x) => x.key === key);
      return v ? { label: this.t(v.labelKey), unit: v.unit } : null;
    };

    const primary = meta(input.metricField);
    if (!primary) return;
    const hasSecondary = body.some((d) => d.axis === 'secondary');
    const secondary = hasSecondary ? meta(input.secondaryMetricField) : null;

    const channelWord = this.t('measurement.shm.channel');
    const datasets: ExplorerChartDataset[] = body.map((d) => ({
      label: composeSeriesLabel(d, d.axis === 'secondary' && secondary ? secondary.label : primary.label, channelWord),
      datasetLabel: d.datasetLabel,
      axis: d.axis,
      dataPoints: d.dataPoints,
    }));

    // Ranges belong to a variable: a different (or removed) variable starts that axis at auto.
    const next = { primary: input.metricField, secondary: secondary ? input.secondaryMetricField : null };
    const reset = resetRangesOnVariableChange(this.lastKeys, next);
    if (reset.y) this.yRange.set(AUTO_RANGE);
    if (reset.y2) this.y2Range.set(AUTO_RANGE);
    this.lastKeys = next;

    this.primaryAxis.set(primary);
    this.secondaryAxis.set(secondary);
    this.lastResult = { datasets, timeAgg: input.timeAggregationKey };
    this.resultTimeAgg.set(input.timeAggregationKey);

    const typeLabel = this.t('measurement.types.' + (input.measurementType ?? ''));
    const labelOf = (list: Array<{ key: string; label: string }>, key: string) => list.find((o) => o.key === key)?.label ?? '';
    this.querySummary.set({
      text: `${typeLabel} · ${primary.label}${secondary ? ' + ' + secondary.label : ''}`,
      badges: buildQueryBadges(input, {
        timeAggregation: labelOf(this.timeAggregations, input.timeAggregationKey),
        aggregation: labelOf(this.aggregationFunctions, input.aggregationKey),
      }).map((b) => [this.t('monitoring.data-explorer.summary.' + b.key), b.count, b.detail].filter((x) => x !== undefined).join(' · ')),
    });
    this.queryCollapsed.set(true);

    this.hasData.set(true);
    this.renderChart();
  }

  // ── chart ────────────────────────────────────────────────────────────────
  private renderChart() {
    this.destroyChart();
    const result = this.lastResult;
    const primary = this.primaryAxis();
    const ctx = this.chartCanvas()?.nativeElement.getContext('2d');
    if (!result || !primary || !ctx) return;

    this.chart = new Chart(ctx, {
      ...buildExplorerChartConfig({
        datasets: result.datasets,
        timeAgg: result.timeAgg,
        primary,
        secondary: this.secondaryAxis(),
        ranges: { y: this.yRange(), y2: this.y2Range() },
        onLegendChange: (items) => this.legendItems.set(items),
      }),
      plugins: [HtmlLegendPlugin],
    });
  }

  /** Display settings: apply an axis range live (in memory only). */
  onRangeChange(axis: 'y' | 'y2', cfg: ChartOptionsConfig) {
    (axis === 'y' ? this.yRange : this.y2Range).set(cfg);
    const scale = this.chart?.options.scales?.[axis];
    if (scale && this.chart) {
      applyRangeToScale(scale as Record<string, any>, cfg);
      this.chart.update();
    }
  }

  /** Legend click: show/hide a series (HtmlLegendPlugin then refreshes `legendItems`). */
  onLegendToggle(datasetIndex: number) {
    if (!this.chart) return;
    this.chart.setDatasetVisibility(datasetIndex, !this.chart.isDatasetVisible(datasetIndex));
    this.chart.update();
  }

  // ── export ───────────────────────────────────────────────────────────────
  private exportName(ext: string): string {
    const stem = (this.lastKeys.primary ?? 'chart') + (this.lastKeys.secondary ? `-${this.lastKeys.secondary}` : '');
    return `explorer-${stem}-${Date.now()}.${ext}`;
  }

  /** PNG of the chart, with the custom legend stitched underneath (no legend in bar mode). */
  exportPng() {
    const canvas = this.chartCanvas()?.nativeElement;
    if (!canvas || !this.chart) return;
    const composed = composeChartWithLegend(canvas, this.showLegend() ? this.legendItems() : []);
    downloadDataUrl(composed.toDataURL('image/png'), this.exportName('png'));
  }

  /** Long-format CSV of what is displayed (series hidden in the legend are left out). */
  exportCsv() {
    const result = this.lastResult;
    const primary = this.primaryAxis();
    if (!result || !primary) return;
    const csv = buildExplorerCsv({
      datasets: result.datasets,
      timeAgg: result.timeAgg,
      primary,
      secondary: this.secondaryAxis(),
      hiddenIndexes: this.legendItems().filter((i) => i.hidden).map((i) => i.datasetIndex),
    });
    downloadTextFile('\uFEFF' + csv, this.exportName('csv')); // BOM so Excel reads ºC / accents as UTF-8
  }

  private destroyChart() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
    this.legendItems.set([]);
  }
}
